import {
    faArrowLeftToLine,
    faBolt,
    faChevronRight,
    faFolder,
    faMagnifyingGlass,
    faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { UsageMeter } from './UsageMeter.tsx';
import Button from '../../core/components/Button.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { formatRelativeDay } from '../utils/relativeDate.ts';

const PlusIcon = withIcon(faPlus);
const CollapseIcon = withIcon(faArrowLeftToLine);
const ProjectsIcon = withIcon(faFolder);
const BoltIcon = withIcon(faBolt);
const SearchIcon = withIcon(faMagnifyingGlass);
const SectionToggleIcon = withIcon(faChevronRight);

const DUMMY_USAGE_PERCENT = 40;

export interface PreviewConversation {
    title: string;
    updatedAt: string;
}

const sectionLabel = 'flex items-center gap-1.5 px-2 pb-1 pt-3 text-xs font-medium tracking-wide text-content-medium';

const SectionHeader = ({ label, expanded }: { label: string; expanded: boolean }) => (
    <span className={sectionLabel}>
        {label}
        <SectionToggleIcon className={classnames('text-xs', expanded && 'rotate-90')} />
    </span>
);

const NavRow = ({ icon, label }: { icon: ReactNode; label: string }) => (
    <span className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-content-strong">
        <span className="flex w-4 flex-none justify-center">{icon}</span>
        <span className="min-w-0 flex-1 truncate">{label}</span>
    </span>
);

const DisabledSidebar = ({ conversations = [] }: { conversations?: PreviewConversation[] }) => {
    const { t } = useTranslation('chat');

    return (
        <div
            aria-hidden
            inert
            className="flex w-72 flex-none select-none flex-col overflow-hidden border-0 border-r border-solid border-line-weak bg-surface-weak"
        >
            <div className="flex flex-1 flex-col opacity-50 grayscale">
                <div className="flex items-center gap-2 px-4 pb-2 pt-4">
                    <span className="text-base font-semibold text-content-strong">{t('title')}</span>
                    <span className="ml-auto flex h-7 w-7 items-center justify-center text-content-weak">
                        <CollapseIcon />
                    </span>
                </div>
                <div className="px-4 pb-2">
                    <Button variant="primary" className="block w-full text-center">
                        <PlusIcon className="mr-1.5" />
                        {t('sidebar.newConversation')}
                    </Button>
                </div>
                <div className="px-4 pb-3">
                    <StringField
                        type="search"
                        prefix={<SearchIcon />}
                        placeholder={t('sidebar.searchPlaceholder')}
                        value=""
                        onValueChange={() => {}}
                    />
                </div>
                <div className="flex flex-col gap-0.5 px-4 pb-2">
                    <NavRow icon={<ProjectsIcon />} label={t('sidebar.projects')} />
                    <NavRow icon={<BoltIcon />} label={t('automations.title')} />
                </div>
                <div className="flex-1 overflow-hidden px-2 pb-4">
                    <SectionHeader label={t('sidebar.sharedWithMe')} expanded={false} />
                    <SectionHeader label={t('sidebar.recent')} expanded />
                    {conversations.map(({ title, updatedAt }, index) => (
                        <div key={`${updatedAt}-${index}`} className="flex flex-col gap-0.5 rounded px-3 py-2">
                            <span className="truncate text-sm font-medium text-content-strong">
                                {title || t('sidebar.untitled')}
                            </span>
                            <span className="truncate text-xs text-content-medium">
                                {formatRelativeDay(updatedAt, t)}
                            </span>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-3 border-0 border-t border-solid border-line-weak px-4 py-3">
                    <span className="flex-none text-xs text-content-weak">{t('sidebar.usage')}</span>
                    <UsageMeter percent={DUMMY_USAGE_PERCENT} className="flex-1" />
                </div>
            </div>
        </div>
    );
};

export const TeaserChatPreview = ({
    conversations,
    children,
}: {
    conversations?: PreviewConversation[];
    children: ReactNode;
}) => (
    <div className="flex min-w-0 flex-1">
        <DisabledSidebar conversations={conversations} />
        <div className="flex min-w-0 flex-1 justify-center overflow-y-auto px-6 py-6">{children}</div>
    </div>
);

export default TeaserChatPreview;
